import React, { useEffect, useState, useMemo } from "react";
import { getImageUrl } from "../../../commons/assets_path";
import styles from "./SettingModal.module.scss";
import { SettingMenu } from "../../../constants/settings";

import AccountSettings from "./SettingPages/AccountSettings";
import CommonSettings from "./SettingPages/CommonSettings";
import CompanyBillingInfo from "./SettingPages/CompanyBillingInfo";
import GuestManagement from "./SettingPages/GuestManagement";
import MemberManagement from "./SettingPages/MemberManagement";
import NotificationSettings from "./SettingPages/NotificationSettings";
import PlanAndBilling from "./SettingPages/PlanAndBilling";
import TwoFactorAuthSettings from "./SettingPages/TwoFactorAuthSettings";
import SecuritySettings from "./SettingPages/SecuritySettings";

import { getCurrentPage } from "../../../reducks/store/Settings/selectors";
import { setCurrentPage } from "../../../reducks/store/Settings/slices";
import { useSelector, useDispatch } from "react-redux";

import type { RootState, AppDispatch } from "../../../reducks/store/Settings/store";
import type { SettingsMenuType, NoticeType } from "../../../types/settingsTypes";
import axios from "axios";
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
import { useTranslation } from "react-i18next";

type Props = {
  client: {
    code: string;
    id: number;
    plan_code: string;
    country_code: string;
  };
  isPartner: boolean;
  client_member_id: number;
  memberId: number;
  service_icon: {
    slack: string;
    chatwork: string;
  };
  myAccount: {
    email: string;
    name: string;
    id: number;
    role: string;
  };
  updateTaskLimitPolicy: boolean;
  isAdmin: boolean;
  new_member_session_path: string;
  members_count: number;
  client_member_members_policy: boolean;
  stripe_billing_edit_policy: boolean;
  subscription_show_policy: boolean;
  secure_ip_retrieve_policy: boolean;
  login_setting_retrieve_policy: boolean;
  workspaces_path: string;
  workspaceLinkPolicy: boolean;
  is_internal: boolean;
  destroy_member_session_path: string;
  locale: string;
  changeModalCss: (width: string, height: string) => void;
  setHeaderShow: (show: boolean) => void;
};

const SettingContent = ({
  client,
  isPartner,
  client_member_id,
  service_icon,
  updateTaskLimitPolicy,
  isAdmin,
  new_member_session_path,
  myAccount,
  members_count,
  memberId,
  client_member_members_policy,
  stripe_billing_edit_policy,
  subscription_show_policy,
  secure_ip_retrieve_policy,
  login_setting_retrieve_policy,
  workspaces_path,
  workspaceLinkPolicy,
  is_internal,
  destroy_member_session_path,
  locale,
  changeModalCss,
  setHeaderShow,
}: Props) => {
  const [notice, setNotice] = useState<NoticeType>(null);
  const [sideMenuKey, setSideMenuKey] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  const { t } = useTranslation();

  const selector = useSelector((state: RootState) => state);
  const dispatch = useDispatch<AppDispatch>();
  const currentPage = getCurrentPage(selector);

  const sectionMenu = (type: "account" | "member") => {
    const accountType = ["account", "notification", "two-factor-auth"];

    return type === "account"
      ? SettingMenu.filter((item) => accountType.includes(item.type))
      : type === "member"
      ? SettingMenu.filter(
          (item) =>
            !accountType.includes(item.type) &&
            !(item.type === "member-management" && !client_member_members_policy) &&
            !(item.type === "guest-management" && !client_member_members_policy) &&
            !(item.type === "common" && !client_member_members_policy) &&
            !(item.type === "company-billing-info" && !stripe_billing_edit_policy) &&
            !(item.type === "plan-and-billing" && !subscription_show_policy) &&
            !(item.type === "security" && !secure_ip_retrieve_policy && !login_setting_retrieve_policy),
        )
      : [];
  };

  const shouldShowTeamSection = useMemo(() => {
    return (
      myAccount.role !== "member" &&
      (client_member_members_policy ||
        stripe_billing_edit_policy ||
        subscription_show_policy ||
        secure_ip_retrieve_policy ||
        login_setting_retrieve_policy)
    );
  }, [
    myAccount.role,
    client_member_members_policy,
    stripe_billing_edit_policy,
    subscription_show_policy,
    secure_ip_retrieve_policy,
    login_setting_retrieve_policy,
  ]);

  const handleMenuClick = (type: SettingsMenuType) => {
    dispatch(setCurrentPage(type));
    // コンポーネントの強制再レンダリング
    setSideMenuKey((prevKey) => prevKey + 1);
  };

  const SwitchComponent = useMemo(() => {
    switch (currentPage) {
      case "account":
        return (
          <AccountSettings
            key={`account-${sideMenuKey}`}
            client={client.code}
            setNotice={setNotice}
            memberId={memberId}
            changeModalCss={changeModalCss}
            setIsMenuOpen={setIsMenuOpen}
            setHeaderShow={setHeaderShow}
          />
        );
      case "common":
        return (
          <CommonSettings
            key={`common-${sideMenuKey}`}
            client={client}
            updateTaskLimitPolicy={updateTaskLimitPolicy}
            isAdmin={isAdmin}
            setNotice={setNotice}
            new_member_session_path={new_member_session_path}
            changeModalCss={changeModalCss}
            setIsMenuOpen={setIsMenuOpen}
            setHeaderShow={setHeaderShow}
          />
        );
      case "company-billing-info":
        return (
          <CompanyBillingInfo
            key={`company-billing-info-${sideMenuKey}`}
            client={client.code}
            isPartner={isPartner}
            is_internal={is_internal}
            setNotice={setNotice}
            countryCode={client.country_code}
            locale={locale}
          />
        );
      case "guest-management":
        return (
          <GuestManagement
            key={`guest-management-${sideMenuKey}`}
            client={client.code}
            changeModalCss={changeModalCss}
            setIsMenuOpen={setIsMenuOpen}
            setHeaderShow={setHeaderShow}
          />
        );
      case "member-management":
        return (
          <MemberManagement
            key={`member-management-${sideMenuKey}`}
            client={client.code}
            countryCode={client.country_code}
            myAccount={myAccount}
            changeModalCss={changeModalCss}
            setIsMenuOpen={setIsMenuOpen}
            setHeaderShow={setHeaderShow}
          />
        );
      case "notification":
        return (
          <NotificationSettings
            key={`notification-${sideMenuKey}`}
            client={client}
            client_member_id={client_member_id}
            service_icon={service_icon}
            setNotice={setNotice}
            changeModalCss={changeModalCss}
            setIsMenuOpen={setIsMenuOpen}
            setHeaderShow={setHeaderShow}
          />
        );
      case "plan-and-billing":
        return (
          <PlanAndBilling
            key={`plan-and-billing-${sideMenuKey}`}
            client={client}
            members_count={members_count}
            is_internal={is_internal}
            locale={locale}
          />
        );
      case "security":
        return (
          <SecuritySettings
            key={`security-${sideMenuKey}`}
            client={client}
            changeModalCss={changeModalCss}
            setIsMenuOpen={setIsMenuOpen}
            setHeaderShow={setHeaderShow}
          />
        );
      case "two-factor-auth":
        return (
          <TwoFactorAuthSettings key={`two-factor-auth-${sideMenuKey}`} client={client.code} myAccount={myAccount} />
        );
    }
  }, [currentPage, sideMenuKey]);

  /* NOTE: reducksから参照するようにする */
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const lastCategory = queryParams.getAll("category").slice(-1)[0];
    if (
      lastCategory === "notification" ||
      lastCategory === "account" ||
      lastCategory === "two-factor-auth" ||
      lastCategory === "common" ||
      lastCategory === "plan-and-billing"
    ) {
      dispatch(setCurrentPage(lastCategory as SettingsMenuType));
    }
  }, []);

  useEffect(() => {
    if (notice) {
      setTimeout(() => {
        setNotice(null);
      }, 7000);
    }
  }, [notice]);

  return (
    <div className={`${styles.settingContent}`}>
      {isMenuOpen && (
        <div className={`${styles.settingContentSideMenu}`}>
          <div className={`${styles.settingContentSideMenuWrapper}`}>
            <ul className={`${styles.sideMenu}`}>
              <li>
                <span className={`${styles.sideMenuSectionLabel}`}>{t("SettingContent.account")}</span>
              </li>
              {sectionMenu("account").map((item) => (
                <li
                  className={`${styles.sideMenuItem} ${currentPage === item.type ? styles.active : ""}`}
                  onClick={() => handleMenuClick(item.type)}
                  key={`settings-sidemenu-${item.type}`}
                >
                  <img src={getImageUrl(`images/icons/${item.icon}.svg`)} alt="" width={20} height={20} />
                  <span>{t(item.title)}</span>
                </li>
              ))}
            </ul>
            {shouldShowTeamSection && (
              <ul className={`${styles.sideMenu}`}>
                <li>
                  <span className={`${styles.sideMenuSectionLabel}`}>{t("SettingContent.team")}</span>
                </li>
                {sectionMenu("member").map((item) => (
                  <li
                    className={`${styles.sideMenuItem} ${currentPage === item.type ? styles.active : ""}`}
                    onClick={() => handleMenuClick(item.type)}
                    key={`settings-sidemenu-${item.type}`}
                    data-testid={`settings-sidemenu-${item.type}`}
                  >
                    <img
                      src={getImageUrl(`images/icons/${item.icon}.svg`)}
                      alt=""
                      width={20}
                      height={20}
                      style={{ ...(item.type === "security" && { width: 17, height: 17 }) }}
                    />
                    <span>{t(item.title)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <ul className={`${styles.sideMenu}`}>
            <li data-testid="test-signout-btn">
              <a
                href={destroy_member_session_path}
                data-confirm={t("loggedInHeader.logout_confirmation")}
                rel="nofollow"
                data-method="delete"
                className={`${styles.sideMenuItem}`}
                data-testid="test-signout-btn"
              >
                <img src={getImageUrl(`images/icons/signout.svg`)} alt="" width={20} height={20} />
                <span>{t("SettingContent.ログアウト")}</span>
              </a>
            </li>
          </ul>
        </div>
      )}
      <div
        className={`${styles.settingContentMain}`}
        id="setting-content-main"
        style={{ height: isMenuOpen ? "670px" : "100%", padding: isMenuOpen ? "34px 80px" : "40px" }}
      >
        {notice && (
          <div className="flash_message notice" style={{ zIndex: 9999 }}>
            {notice.message.split("/n").map((message) => (
              <>
                {message}
                <br />
              </>
            ))}
          </div>
        )}
        {SwitchComponent}
      </div>
    </div>
  );
};

export default SettingContent;
